import { discountHelpers } from "@dotpe/rista-pos-common";
import store from "../../redux-store";
import { addDiscountApprovalData } from "../../redux-store/slices/DiscountSlice";
import { validateManualDiscountLimitFn } from "../saleHelpers";

export * from "./shared";

/**
 * Discount-approval state, desktop half. The body is identical to the other variant's — see
 * `shared.ts` for why it is duplicated rather than shared (this side takes
 * `validateManualDiscountLimitFn` from `../saleHelpers/index.desktop`; the other takes its own from
 * `myOrderHelpers`).
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 */

/**
 * Recompute whether the bill needs manual-discount approval, and record it.
 *
 * ⚠️ `isMyorder` switches the shape of `data`: the my-order flow passes the sale itself, every
 * other caller passes an ARRAY and only `data[0]` is read.
 *
 * ⚠️ In the `else if` branch, `data` refers to the FUNCTION PARAMETER (the destructured `data`
 * is scoped to the `if`), so `data?.manualDiscountTotal` is almost always `undefined` there and
 * the `|| 0` fallback carries it. That is the apps' own behaviour on both platforms; preserved
 * verbatim rather than "fixed".
 */
export const updateDiscountApprovalState = (data: any, isMyorder = false) => {
  const response = isMyorder ? data : data[0]
  const accessPreferences = store.getState().businessStore.userData.accessPreferences || {}
  if (response.approvalRequests) {
    const { data = {} } = response?.approvalRequests[0];
    const updatedApprovalDataFn = discountHelpers.getDiscountApprovalStatusData(response.mid, data?.manualDiscountTotal || 0);
    const { validation, totalDiscount } = discountHelpers.discountLimitCheck(response, accessPreferences, data.manualDiscountTotal, validateManualDiscountLimitFn());
    if (!validation) return;
    if (totalDiscount <= data.manualDiscountTotal) {
      const updatedApporvalData = updatedApprovalDataFn(response.approvalRequests[0]);
      store.dispatch(addDiscountApprovalData(updatedApporvalData));
    }
  }
  else if (accessPreferences?.discountPercentLimit) {
    const { validation, totalDiscount, shouldRender } = discountHelpers.discountLimitCheck(response, accessPreferences, 0, validateManualDiscountLimitFn());
    if (!validation || !shouldRender) return;
    // not requested yet; but request is required
    const updatedApprovalDataFn = discountHelpers.getDiscountApprovalStatusData(response.mid, (data as any)?.manualDiscountTotal || 0);
    store.dispatch(addDiscountApprovalData(updatedApprovalDataFn(response.approvalRequests[0])));
  }
}

/**
 * Re-evaluate approval state after a recalculation.
 *
 * ⚠️ **Only writes when `shouldRender` is FALSE** — i.e. it exists to RESET approval data, not to
 * request it. The comment is the apps' own and the asymmetry is deliberate.
 */
export function checkAndUpdateApprovalState(saleData: any) {
  const currentDiscountApprovalData = discountHelpers.getCurrentDiscountApprovalData(store.getState().discountStore.discountApproval, saleData.mid);
  const { shouldRender, totalDiscount } = discountHelpers.discountLimitCheck(saleData, store.getState().businessStore.userData.accessPreferences || {}, saleData.discountTotal, validateManualDiscountLimitFn());
  const updatedApprovalData = discountHelpers.getUpdatedDiscountApprovalData({ shouldRender, mid: saleData.mid, currentDiscountApprovalData, totalDiscount });
  if (!shouldRender) //only update here, if we are trying to reset data.
    store.dispatch(addDiscountApprovalData(updatedApprovalData));
}
